import { AddIcon } from "@chakra-ui/icons";
import { Button, VStack } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  LibraryElementTypeDto,
  type LibraryElementContentDto,
  type MinimumRequirementDto,
} from "../../autogen/bff-api";
import { useConfirmationModal } from "../../common/useConfirmationModal";
import { MinimumRequirementList } from "../sourcing-events/buyer/wizard/components/MinimumRequirementList";
import { MinimumRequirementModal, type MinimumRequirementFormValues } from "../kravbibliotek/MinimumRequirementModal";
import { AddedFromLibraryBanner } from "../../common/library/AddedFromLibraryBanner";
import { AddRequirementPanel } from "../../common/library/AddRequirementPanel";
import { useAddFromLibrary } from "../../common/library/useAddFromLibrary";
import { SectionEmptyState } from "../../common/library/SectionEmptyState";
import { TemplateSectionCard } from "./TemplateSectionCard";

// t('Minimum requirements') t('Absolute requirements that must be met for a bid to be evaluated.')
// t('Add') t('Add minimum requirements') t('minimum requirements')
// t('No minimum requirements in this template')
// t('Delete requirement') t('Are you sure you want to delete this requirement?') t('Delete')

interface Props {
  items: MinimumRequirementDto[];
  onChange: (items: MinimumRequirementDto[]) => void;
}

type ModalState = { mode: "edit"; index: number; initialValues: MinimumRequirementFormValues };

export const TemplateMinimumSection = ({ items, onChange }: Props) => {
  const { t } = useTranslation();
  const confirmationModal = useConfirmationModal();
  const [modal, setModal] = useState<ModalState | null>(null);
  const [modalKey, setModalKey] = useState(0);
  const library = useAddFromLibrary({ items, commit: onChange });
  const { save } = library;



  const openEdit = (index: number) => {
    const item = items[index];
    setModalKey((k) => k + 1);
    setModal({
      mode: "edit",
      index,
      initialValues: {
        responseType: item.responseType,
        title: item.title,
        description: item.description,
      },
    });
  };

  const handleSubmit = async (values: Omit<MinimumRequirementDto, "id">) => {
    if (modal) save(items.map((it, i) => (i === modal.index ? { ...it, ...values } : it)));
    setModal(null);
  };

  const handleDelete = (index: number) => {
    confirmationModal.show({
      title: t("Delete requirement"),
      description: t("Are you sure you want to delete this requirement?"),
      confirmButtonText: t("Delete") ?? "",
      confirmButtonColor: "red",
      onConfirmed: () => save(items.filter((_, i) => i !== index)),
    });
  };

  const addFromLibrary = (contents: LibraryElementContentDto[]) =>
    library.add(contents.flatMap((c) => (c.minimum ? [{ ...c.minimum, id: undefined }] : [])));

  return (
    <TemplateSectionCard
      title={t("Minimum requirements")}
      count={items.length}
      description={t("Absolute requirements that must be met for a bid to be evaluated.")}
    >
      <VStack align="stretch" spacing="4">
        {library.lastAdded && <AddedFromLibraryBanner count={library.lastAdded.count} onUndo={library.undo} />}
        {items.length === 0 ? (
          <SectionEmptyState
            title={t("No minimum requirements in this template")}
            type={LibraryElementTypeDto.Minimum}
            onAddFromLibrary={() => library.openPanel("library")}
            onWriteNew={() => library.openPanel("new")}
          />
        ) : (
          <>
            <MinimumRequirementList
              items={items}
              onAdd={() => library.openPanel("library")}
              onEdit={openEdit}
              onDelete={handleDelete}
              markedCount={library.markedCount}
            />
            <Button
              variant="outline"
              size="sm"
              leftIcon={<AddIcon />}
              onClick={() => library.openPanel("library")}
              alignSelf="flex-start"
            >
              {t("Add")}
            </Button>
          </>
        )}
      </VStack>

      <AddRequirementPanel
        isOpen={library.isPanelOpen}
        onClose={library.closePanel}
        type={LibraryElementTypeDto.Minimum}
        title={t("Add minimum requirements")}
        typeNoun={t("minimum requirements")}
        isAlreadyInSection={(el) => items.some((i) => i.title === el.content.minimum?.title)}
        initialTab={library.tab}
        onAdd={addFromLibrary}
      />

      <MinimumRequirementModal
        key={modalKey}
        isOpen={modal !== null}
        isSaving={false}
        mode="edit"
        defaultValues={modal?.initialValues}
        onSubmit={handleSubmit}
        onClose={() => setModal(null)}
      />
      {confirmationModal.modal}
    </TemplateSectionCard>
  );
};
